import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
    ArrowRightIcon, CheckCircleIcon, ChevronDownIcon, ChevronUpIcon, MapIcon,
} from '@heroicons/react/24/outline';
import { CheckCircleIcon as CheckSolid } from '@heroicons/react/24/solid';
import { useAuth } from '../context/AuthContext';
import { gradeNumber } from '../utils/gradeYear';
import { YEAR_PLANS, movesFor } from '../constants/yearPlans';
import { track, EVENTS } from '../services/analytics';
import { isEstimatedFit, isListLocked } from '../utils/listBalance';
import {
    fetchStructuredProfile, getCollegeList, getMajorMap, getActivityIdeas,
} from '../services/api';

/**
 * "Your Year" — the grade-differentiated plan that keeps a student engaged
 * after onboarding ends (founder, 2026-08-04: "we have to have a strategy for
 * each year of students and showcase the core features that would be useful
 * for each one of these market segments").
 *
 * The problem it solves: WelcomePage routes by grade, and then that awareness
 * evaporates. A freshman returning on day two met a senior's product — add
 * colleges, read your odds — which is wrong for them AND spends their free
 * credits on a question four years early. That mismatch is the most likely
 * reason retention past day one is near zero.
 *
 * Every move points at a feature that already exists. Done-state is derived
 * from REAL data (a major map exists, activities were generated, colleges
 * were added, fits were read) — never from having visited a page, which
 * measures a tour of our surfaces rather than value to the student.
 *
 * Renders NOTHING without a known grade: a wrong year's plan is worse than no
 * plan, and the honest fix is to ask for the grade, which the profile flow
 * already does.
 */

const openKey = (email) => `yy_open_${email}`;

export default function YourYearPlan() {
    const { currentUser } = useAuth();
    const email = currentUser?.email;
    const [grade, setGrade] = useState(null);
    const [gradYear, setGradYear] = useState(null);
    const [done, setDone] = useState({});
    const [loaded, setLoaded] = useState(false);
    const [open, setOpen] = useState(true);

    useEffect(() => {
        if (!email) return;
        try {
            const pref = localStorage.getItem(openKey(email));
            if (pref !== null) setOpen(pref === '1');
        } catch { /* private mode */ }
    }, [email]);

    const load = useCallback(async () => {
        if (!email) return;
        try {
            // Everything in parallel, and every read independently optional:
            // a plan that renders with one unknown beats no plan at all.
            const [profile, colleges, majorMap, activities] = await Promise.all([
                fetchStructuredProfile(email).catch(() => null),
                getCollegeList(email).catch(() => null),
                getMajorMap(email).catch(() => null),
                getActivityIdeas(email).catch(() => null),
            ]);
            const p = profile?.profile || profile || {};
            const g = gradeNumber(p.grade) ?? gradeNumber(p.grade_level);
            setGrade(g);
            setGradYear(p.graduation_year || null);
            const list = colleges?.colleges || colleges?.college_list || [];
            setDone({
                // The REAL envelopes: /get-major-map returns {map|null} and
                // /get-activity-ideas returns {ideas_doc|null} — these two
                // read phantom keys for two weeks and could never tick
                // (Marcus run). Old keys kept as fallbacks, not as the claim.
                major_map: !!(majorMap?.map || majorMap?.major_map || majorMap?.majors?.length),
                activities: !!(activities?.ideas_doc?.ideas?.length
                    || activities?.ideas?.length || activities?.activity_ideas?.length),
                // A senior's move is "Lock your list": the Season moment's own
                // rule (balanced, 10+ schools, every one read), never "any
                // school" (#3734). Earlier years' moves are building one.
                colleges: g === 12 ? isListLocked(list) : list.length > 0,
                // A row carries its personal band as `fit_category`; the old
                // fit_analysis / fit_score / has_fit keys are never on a list
                // row, so this never ticked (#3734). A senior reads odds "at
                // every school".
                fits: g === 12
                    ? list.length > 0 && list.every((c) => !isEstimatedFit(c))
                    : list.some((c) => !isEstimatedFit(c)),
                // Summer and roadmap have no single completion artifact — they
                // are places to think, not tasks to finish. Deliberately never
                // "done": a checkbox here would reward a page visit.
            });
        } catch { /* the card simply stays quiet */ }
        finally { setLoaded(true); }
    }, [email]);

    useEffect(() => { load(); }, [load]);

    const plan = grade ? YEAR_PLANS[grade] : null;
    if (!loaded || !plan) return null;

    const moves = movesFor(grade);
    const doneCount = moves.filter((m) => done[m.key]).length;
    const next = moves.find((m) => !done[m.key]);

    const toggle = () => {
        setOpen((o) => {
            try { localStorage.setItem(openKey(email), o ? '0' : '1'); } catch { /* ignore */ }
            return !o;
        });
    };

    return (
        <div className="mb-6 rounded-2xl border border-[#2D6B45] bg-white overflow-hidden shadow-sm"
             data-testid="your-year-plan">
            <button onClick={toggle} aria-expanded={open} data-testid="your-year-toggle"
                    className="flex w-full items-center gap-3 bg-[#2D6B45] px-5 py-4 text-left hover:bg-[#1A4D2E]">
                <span className="grid h-9 w-9 shrink-0 place-items-center rounded-xl bg-white/15 text-white">
                    <MapIcon className="h-5 w-5" />
                </span>
                <span className="min-w-0 flex-1">
                    <span className="flex flex-wrap items-baseline gap-2">
                        <span className="font-serif text-lg font-bold text-white">
                            {plan.headline}
                        </span>
                        <span className="rounded-full bg-white/20 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-white"
                              data-testid="your-year-grade">
                            {plan.label}{gradYear ? ` · class of ${gradYear}` : ''}
                        </span>
                    </span>
                    <span className="mt-0.5 block truncate text-xs text-[#D6E8D5]">
                        {open ? plan.thesis
                            : (next ? `Next: ${next.title}` : 'Every move for this year is underway.')}
                    </span>
                </span>
                <span className="shrink-0 text-xs font-semibold text-[#D6E8D5]" data-testid="your-year-progress">
                    {doneCount}/{moves.length}
                </span>
                {open ? <ChevronUpIcon className="h-4 w-4 shrink-0 text-[#D6E8D5]" />
                    : <ChevronDownIcon className="h-4 w-4 shrink-0 text-[#D6E8D5]" />}
            </button>

            {open && (
                <div className="border-t border-[#F0EEE8] px-5 py-4">
                    <ul className="space-y-2.5">
                        {moves.map((m) => {
                            const isDone = !!done[m.key];
                            return (
                                <li key={m.key}>
                                    <Link to={m.to} data-testid={`your-year-move-${m.key}`}
                                          onClick={() => track(EVENTS.YEAR_PLAN_MOVE_CLICKED,
                                              { move: m.key, grade, was_done: isDone })}
                                          className="flex gap-3 rounded-xl border border-[#F0EEE8] p-3 hover:border-[#A8C5A6] hover:bg-[#FCFBF7]">
                                        {isDone
                                            ? <CheckSolid className="h-5 w-5 shrink-0 text-[#1A4D2E]" />
                                            : <CheckCircleIcon className="h-5 w-5 shrink-0 text-[#C9C5BB]" />}
                                        <span className="min-w-0 flex-1">
                                            <span className="flex flex-wrap items-center gap-2">
                                                <span className={`text-sm font-semibold ${isDone ? 'text-[#6B6B6B]' : 'text-[#2C2C2C]'}`}>
                                                    {m.title}
                                                </span>
                                                {/* The 'free' chip was removed (founder 2026-08-04:
                                                    no cost talk outside /pricing). m.free stays as
                                                    ordering metadata — underclassmen still LEAD with
                                                    the no-cost moves; we just don't say why. */}
                                                {isDone && (
                                                    <span className="text-[11px] font-semibold text-[#1A4D2E]">done — revisit anytime</span>
                                                )}
                                            </span>
                                            <span className="mt-0.5 block text-xs leading-relaxed text-[#6B6B6B]">
                                                {m.why}
                                            </span>
                                        </span>
                                        <ArrowRightIcon className="h-4 w-4 shrink-0 self-center text-[#9A968C]" />
                                    </Link>
                                </li>
                            );
                        })}
                    </ul>
                    <p className="mt-3.5 rounded-xl bg-[#F0F7F1] px-4 py-2.5 text-xs leading-relaxed text-[#2C2C2C]"
                       data-testid="your-year-pitch">
                        {plan.pitch}
                    </p>
                </div>
            )}
        </div>
    );
}
